import { createContext, createElement, useCallback, useContext, useEffect, useState } from "react";

export const STORAGE_KEY = "ugcgo.premiere-video.v1";
export const OWNER_KEY = "ugcgo.owner";
export const EXPORT_APP = "ugc-go";
export const EXPORT_MODULE = "premiere-video";

export const emptyState = () => ({
  m1: { answers: {}, submitted: false },
  m2: { name: "", category: "", why: "", utility: "", detail: "" },
  m3: { angle: "", audience: "", situation: "", show: "", benefit: "", idea: "" },
  m4: { hook: "", demo: "", conclusion: "" },
  m5: { notes: {}, filmed: {}, checklist: {} },
  m6: { steps: {}, tool: "", location: "" },
  m7: { evals: {}, strengths: "", improvements: "", learned: "", certName: "", certDate: "" },
  completed: {},
});

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

export function normalize(raw) {
  const base = emptyState();
  if (!isObj(raw)) return base;
  Object.keys(base).forEach((k) => {
    if (!isObj(raw[k])) return;
    Object.keys(base[k]).forEach((f) => {
      const v = raw[k][f];
      if (v !== undefined && typeof v === typeof base[k][f] && Array.isArray(v) === Array.isArray(base[k][f])) base[k][f] = v;
    });
  });
  if (isObj(raw.completed)) base.completed = raw.completed;
  return base;
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? normalize(JSON.parse(raw)) : emptyState();
  } catch {
    return emptyState();
  }
}

const Ctx = createContext(null);

export function ProgressProvider({ children }) {
  const [state, setState] = useState(load);
  const [savedAt, setSavedAt] = useState(null);
  const [sync, setSync] = useState("local");

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      setSavedAt(new Date());
    } catch {
      setSavedAt(null);
    }
  }, [state]);

  const update = useCallback((key, patch) => setState((s) => ({ ...s, [key]: { ...s[key], ...patch } })), []);
  const setIn = useCallback(
    (key, field, sub, value) =>
      setState((s) => ({ ...s, [key]: { ...s[key], [field]: { ...s[key][field], [sub]: value } } })),
    []
  );
  const setCompleted = useCallback((id, v) => setState((s) => ({ ...s, completed: { ...s.completed, [id]: v } })), []);
  const replaceAll = useCallback((data) => setState(normalize(data)), []);
  const reset = useCallback(() => setState(emptyState()), []);

  const value = { state, savedAt, sync, setSync, update, setIn, setCompleted, replaceAll, reset };
  return createElement(Ctx.Provider, { value }, children);
}

export const useProgress = () => useContext(Ctx);
